import ComposedChart, {
  ChartDataPoint,
  ChartSeries,
  TooltipContentType,
} from '@/components/Chart/ComposedChart';
import { Card } from '@/components/ui/card';
import { CHART_BAR_COLOR, CHART_LINE_COLOR } from '@/constants/chart';
import { TopMarket } from '@/service/type';
import { formatCurrencyAxis, formatCurrencyFull } from '@/utils/numberUtil';
import { normalizeTokenName } from '@/utils/stringUtils';
import { formatTooltipDate } from '@/utils/timeUtils';
import { BarChart3 } from 'lucide-react';
import React, { useMemo, useState } from 'react';

interface VolumeDataPoint {
  date: string;
  volumeUsd: string;
  cumulativeVolumeUsd: string;
  topMarkets?: TopMarket[];
}

interface TradingVolumeChartProps {
  data?: VolumeDataPoint[];
  height?: number;
  topMarketsLabel?: string;
  showTopMarkets?: boolean;
}

/**
 * Create Custom Tooltip for Trading Volume Chart
 */
const createVolumeTooltip = (topMarketsLabel: string, showTopMarkets: boolean): TooltipContentType => ({ active, payload }) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const data = payload[0]?.payload;
  if (!data) return null;

  const daily = data.daily as number;
  const cumulative = data.cumulative as number;
  const topMarkets = (data.topMarkets || []) as TopMarket[];

  return (
    <div className="bg-solid-toast-tips rounded-lg p-3 shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)]">
      {/* Date */}
      <div className="text-xs font-semibold text-text-black leading-3 mb-3">
        {formatTooltipDate(data.date)}
      </div>

      {/* Cumulative and Daily values */}
      <div className="flex flex-col gap-2 text-xs text-text-black leading-3">
        <div>Cumulative Volume: {formatCurrencyFull(cumulative)}</div>
        <div>Daily Volume: {formatCurrencyFull(daily)}</div>
      </div>

      {/* Divider */}
      {showTopMarkets && topMarkets.length > 0 && (
        <>
          <div className="border-t border-border-light-line-1 my-3" />

          {/* Top Markets */}
          <div className="flex flex-col gap-2 text-xs leading-3">
            <div className="text-text-black">{topMarketsLabel}</div>
            {topMarkets.slice(0, 3).map((market, index) => (
              <div key={index} className="text-text-main-body">
                {normalizeTokenName(market.market)}:{' '}
                {formatCurrencyFull(Math.round(parseFloat(market.volumeUsd)))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Trading Volume Chart Component
 * Displays daily volume (bar) and cumulative volume (line)
 */
const TradingVolumeChart: React.FC<TradingVolumeChartProps> = ({ data, height = 232, topMarketsLabel = 'Top Markets', showTopMarkets = true }) => {
  const [visibleSeries, setVisibleSeries] = useState<Record<string, boolean>>({
    daily: true,
    cumulative: true,
  });

  const VolumeTooltip = useMemo(() => createVolumeTooltip(topMarketsLabel, showTopMarkets), [topMarketsLabel, showTopMarkets]);

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map(item => ({
      date: item.date,
      daily: Math.round(parseFloat(item.volumeUsd)),
      cumulative: Math.round(parseFloat(item.cumulativeVolumeUsd)),
      topMarkets: item.topMarkets || [],
    })) as ChartDataPoint[];
  }, [data]);

  const allSeries: ChartSeries[] = [
    { dataKey: 'daily', name: 'Daily Volume', type: 'bar', color: CHART_BAR_COLOR },
    { dataKey: 'cumulative', name: 'Cumulative Volume', type: 'line', color: CHART_LINE_COLOR },
  ];

  const series = allSeries.filter(s => visibleSeries[s.dataKey]);

  const legends = [
    { dataKey: 'daily', label: 'Daily Volume', color: CHART_BAR_COLOR },
    { dataKey: 'cumulative', label: 'Cumulative Volume', color: CHART_LINE_COLOR },
  ];

  const toggleSeries = (dataKey: string) => {
    setVisibleSeries(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey],
    }));
  };

  const hasData = chartData.length > 0;

  return (
    <Card className="overflow-hidden bg-solid-white-card">
      {/* Header */}
      <div className="p-6 pb-4">
        <h3 className="text-base font-semibold text-text-black h-6 my-0">Trading Volume</h3>
      </div>

      {/* Chart Area */}
      <div className="px-6 pb-4">
        {hasData ? (
          <ComposedChart
            data={chartData}
            series={series}
            height={height}
            yAxisFormatter={formatCurrencyAxis}
            customTooltip={VolumeTooltip}
          />
        ) : (
          <div
            className="flex items-center justify-center border-2 border-dashed border-border-light-line-1 rounded-lg bg-solid-solid2"
            style={{ height: `${height}px` }}
          >
            <div className="text-center">
              <BarChart3 className="h-12 w-12 text-icon-grey mx-auto mb-3" />
              <div className="text-sm text-text-main-body">No Data Available</div>
              <div className="text-xs text-text-prompt mt-1">Trading Volume</div>
            </div>
          </div>
        )}
      </div>

      {/* Legends */}
      <div className="px-6 pb-4 flex items-center justify-center gap-6">
        {legends.map((legend, index) => (
          <div
            key={index}
            className="flex items-center gap-2 cursor-pointer select-none transition-opacity hover:opacity-80"
            onClick={() => toggleSeries(legend.dataKey)}
            style={{ opacity: visibleSeries[legend.dataKey] ? 1 : 0.4 }}
          >
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: legend.color }} />
            <span className="text-sm text-text-main-body">{legend.label}</span>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default TradingVolumeChart;
